import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { participantsByIdAtom } from '../../video-sdk';
import { joiningVisitorIdFamily, visitorAdmissionTimesAtom, waitingVisitorsAtom } from '../state';

export type AdmittedClientStatus = 'sending_invite' | 'preparing' | 'joining' | 'not_on_page' | 'connected';

export type AdmittedClientStatusResult =
  | {
      status: 'connected';
      showSpinner: false;
      shouldPulse: false;
    }
  | {
      status: 'not_on_page';
      showSpinner: false;
      shouldPulse: true;
    }
  | {
      status: Exclude<AdmittedClientStatus, 'connected' | 'not_on_page'>;
      showSpinner: true;
      shouldPulse: false;
    };

interface UseAdmittedClientStatusProps {
  visitorUuid: string;
}

export function useAdmittedClientStatus({ visitorUuid }: UseAdmittedClientStatusProps): AdmittedClientStatusResult {
  const waitingVisitors = useAtomValue(waitingVisitorsAtom);
  const visitorAdmissionTimes = useAtomValue(visitorAdmissionTimesAtom);
  const participantsById = useAtomValue(participantsByIdAtom);
  const isAdmitting = useAtomValue(joiningVisitorIdFamily(visitorUuid));

  const waitingVisitor = waitingVisitors[visitorUuid];
  const providerAdmissionTime = visitorAdmissionTimes?.[visitorUuid];
  const hasParticipant = Boolean(participantsById[visitorUuid]);
  const isInForeground = waitingVisitor?.isInForeground;
  const isJoining = Boolean(waitingVisitor?.isJoining);

  return useMemo(() => {
    if (hasParticipant) {
      return {
        status: 'connected',
        showSpinner: false,
        shouldPulse: false,
      };
    }

    if (isJoining) {
      return {
        status: 'preparing',
        showSpinner: true,
        shouldPulse: false,
      };
    }

    if (isInForeground === false) {
      return {
        status: 'not_on_page',
        showSpinner: false,
        shouldPulse: true,
      };
    }

    if (!waitingVisitor) {
      return {
        status: 'not_on_page',
        showSpinner: false,
        shouldPulse: true,
      };
    }

    if (isAdmitting || providerAdmissionTime) {
      return {
        status: 'sending_invite',
        showSpinner: true,
        shouldPulse: false,
      };
    }

    return {
      status: 'joining',
      showSpinner: true,
      shouldPulse: false,
    };
  }, [hasParticipant, isAdmitting, isInForeground, isJoining, providerAdmissionTime, waitingVisitor]);
}
